/**
 * Pre-init command queue (fbq/gtag stub pattern) for the GTM template and
 * any integration that runs before t.js loads.
 *
 * External code pushes `{m: "track"|"trackEcommerce"|"trackWithPixel"|"pageView"|"setUserData"|"init", a: [...]}`
 * items onto `window.TheSocialTapQ` (a plain array). On takeover we drain the
 * snapshot and override `.push` ON THE SAME ARRAY OBJECT — GTM's createQueue
 * resolves `push` at call time, so replacing the array would strand the
 * sandbox's reference.
 *
 * Two-phase dispatch: setUserData/init run immediately (no config needed);
 * track/pageView buffer in state.pendingCommands until the config fetch
 * settles (success or error), so queued conversions get the dual pixel+CAPI
 * path instead of CAPI-only.
 */

import { state, debugLog } from "./state.js";

export interface QueueCommand {
  m: string;
  a?: unknown[];
}

type QueueArray = QueueCommand[] & { push: (...items: QueueCommand[]) => number };

function api() {
  return (window as Window).TheSocialTap;
}

function dispatch(item: QueueCommand): void {
  const tst = api();
  if (!tst || !item || typeof item.m !== "string") return;
  const args = item.a || [];

  switch (item.m) {
    case "init":
      if (typeof args[0] === "string") tst.init(args[0]);
      return;
    case "trackEcommerce":
      // The WooCommerce plugin pushes this before our script has loaded, which
      // is the normal order: WordPress prints its inline data in the head.
      if (typeof args[0] === "string") {
        tst.trackEcommerce(args[0], args[1] as Record<string, unknown> | undefined,
          typeof args[2] === "string" ? args[2] : undefined);
      }
      return;
    case "setUserData":
      if (args[0] && typeof args[0] === "object") {
        tst.setUserData(args[0] as Record<string, string>);
      }
      return;
    // Toestemming hoeft niet op de configuratie te wachten: de keuze wordt
    // bewaard en geldt vanaf dat moment. Dit pad bestaat zodat een cookiebanner
    // die eerder draait dan ons script toch gehoord wordt, want dat is precies
    // de volgorde waarin een banner normaal gesproken werkt.
    case "consent":
      if (args[0]) tst.consent(args[0] as Parameters<typeof tst.consent>[0]);
      return;
    case "track":
    case "trackWithPixel":
    case "pageView":
      if (!state.configSettled) {
        state.pendingCommands.push(item);
        debugLog("commandQueue", "buffered until config settles", item.m);
        return;
      }
      if (item.m === "track") {
        tst.track(args[0] as string, args[1] as number | Record<string, unknown> | undefined, args[2] as string | undefined);
      } else if (item.m === "trackWithPixel") {
        tst.trackWithPixel(args[0] as string, args[1] as number | undefined, args[2] as string | undefined);
      } else {
        tst.pageView();
      }
      return;
    default:
      debugLog("commandQueue", "unknown command", item.m);
  }
}

/** Take over window.TheSocialTapQ: drain the snapshot, then intercept pushes. */
export function installCommandQueue(): void {
  const w = window as Window & { TheSocialTapQ?: QueueArray };
  const existing = w.TheSocialTapQ;
  const arr: QueueArray = Array.isArray(existing) ? existing : ([] as unknown as QueueArray);

  const snapshot = arr.slice();
  arr.length = 0;
  snapshot.forEach(dispatch);

  arr.push = function (...items: QueueCommand[]): number {
    items.forEach(dispatch);
    return arr.length;
  };

  if (!Array.isArray(existing)) {
    w.TheSocialTapQ = arr;
  }
  debugLog("commandQueue", "installed", "drained=" + snapshot.length);
}

/** Config fetch settled: stop buffering and flush what accumulated. */
export function drainPendingCommands(): void {
  state.configSettled = true;
  const pending = state.pendingCommands.splice(0, state.pendingCommands.length);
  if (pending.length > 0) {
    debugLog("commandQueue", "draining " + pending.length + " pending command(s)");
  }
  pending.forEach(dispatch);
}
